import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { z } from 'zod';
import { DataTable } from '@duke-hq/ui/components/DataTable/index';
import { DataTableSchema, DataTableCellAction } from '@duke-hq/ui/components/DataTable/schema';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { Pagination, IPagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { SortOrder } from '@duke-hq/lib/pagination';
import { FileStatus, FileBucketType, FileScanStatus } from '@duke-hq/svc-api-db';
import FilterBarLayout from '@duke-hq/filters/components/FilterBarLayout';
import FiltersProvider from '@duke-hq/filters/context/FiltersProvider';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { StatusFilter } from './filters/StatusFilter';
import { BucketTypeFilter } from './filters/BucketTypeFilter';
import { MimetypeFilter } from './filters/MimetypeFilter';
import { SizeFilter } from './filters/SizeFilter';
import { ScanStatusFilter } from './filters/ScanStatusFilter';

const fileStatusSchema = z.array(z.nativeEnum(FileStatus));
const bucketTypeSchema = z.array(z.nativeEnum(FileBucketType));
const scanStatusSchema = z.array(z.nativeEnum(FileScanStatus));

function FilesListContent() {
  const { filters } = useFiltersContext();
  const { currentPage, pageSize, setPage } = usePagination();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<{ [key: string]: SortOrder | undefined }>({
    createdAt: SortOrder.DESC,
  });

  const parsedFileStatus = useMemo(() => {
    const result = fileStatusSchema.safeParse(filters['fileStatus']);
    return result.success ? result.data : undefined;
  }, [filters]);

  const parsedBucketType = useMemo(() => {
    const result = bucketTypeSchema.safeParse(filters['bucketType']);
    return result.success ? result.data : undefined;
  }, [filters]);

  const parsedScanStatus = useMemo(() => {
    const result = scanStatusSchema.safeParse(filters['scanStatus']);
    return result.success ? result.data : undefined;
  }, [filters]);

  const { data: filesData } = trpcAdmin.files.listFiles.useQuery({
    table: { sortOrder },
    search: searchQuery.trim() || undefined,
    page: currentPage,
    pageSize,
    status: parsedFileStatus,
    bucketType: parsedBucketType,
    scanStatus: parsedScanStatus,
    mimetype: filters['mimetype'],
    sizeRange: filters['sizeRange'],
  });

  const getDownloadUrl = trpcAdmin.files.getDownloadUrl.useMutation();
  const toast = useToast();

  const onSortChange = useCallback((columnName: string, direction?: SortOrder) => {
    setSortOrder((prev) => {
      if (direction === undefined) {
        const newSortOrder = { ...prev };
        delete newSortOrder[columnName];
        return newSortOrder;
      }
      return { ...prev, [columnName]: direction };
    });
  }, []);

  const handleAction = useCallback(
    async (
      action: DataTableCellAction<{
        fileId: string;
        fileDrn: string;
        filename: string;
      }>,
    ) => {
      if (!action.data) {
        return;
      }

      if (action.kind === 'download') {
        try {
          const result = await getDownloadUrl.mutateAsync({
            fileDrn: action.data.fileDrn,
          });
          if (result.url) {
            window.open(result.url, '_blank');
          }
        } catch (error) {
          toast({
            title: t`Failed to download file`,
            description: error instanceof Error ? error.message : t`Unknown error`,
            status: 'error',
          });
        }
      } else if (action.kind === 'delete') {
        // TODO: Implement delete via admin service
      }
    },
    [getDownloadUrl, toast],
  );

  const data = useMemo(() => {
    if (!filesData) {
      return {
        headers: {},
        rows: [],
      };
    }

    return {
      headers: {
        ...filesData.headers,
        actions: {
          label: '',
          sortable: false,
        },
      },
      rows: filesData.rows.map((row) => {
        const { drn: drnCell, ...restCells } = row.cells;
        const drn = 'value' in drnCell ? drnCell.value : '';
        const isCompleted = row.cells.status.label === 'COMPLETED';

        return {
          id: row.id,
          cells: {
            ...restCells,
            ...(isCompleted && {
              actions: {
                type: 'action' as const,
                label: 'Download',
                kind: 'download',
                data: {
                  fileId: String(row.id),
                  fileDrn: drn,
                  filename: String(row.cells.filename),
                },
                size: 'xs' as const,
                variant: 'ghost' as const,
              },
            }),
          },
        };
      }),
    } satisfies DataTableSchema;
  }, [filesData]);

  const paginationData: IPagination | undefined = useMemo(() => {
    if (!filesData?.pagination || filesData.pagination.type !== 'page') {
      return undefined;
    }

    return {
      currentPage: filesData.pagination.currentPage,
      previousPage:
        filesData.pagination.currentPage > 1 ? filesData.pagination.currentPage - 1 : null,
      nextPage:
        filesData.pagination.currentPage < filesData.pagination.totalPages
          ? filesData.pagination.currentPage + 1
          : null,
      pageCount: filesData.pagination.totalPages,
      totalCount: filesData.pagination.totalItems ?? 0,
      itemsPerPage: pageSize,
      isFirstPage: filesData.pagination.currentPage === 1,
      isLastPage: filesData.pagination.currentPage === filesData.pagination.totalPages,
    };
  }, [filesData, pageSize]);

  return (
    <TableViewLayout
      pageHeader={[
        <PageHeading key="files-list">
          <Trans>Files</Trans>
        </PageHeading>,
      ]}
      filters={
        <>
          <SearchInput
            maxW="300px"
            onCommit={setSearchQuery}
            urlSync={true}
            placeholder={t`Search files...`}
          />
          <FilterBarLayout>
            <MimetypeFilter key="mimetype" />
            <SizeFilter key="size" />
            <BucketTypeFilter key="bucket" />
            <StatusFilter key="status" />
            <ScanStatusFilter key="scanStatus" />
          </FilterBarLayout>
        </>
      }
      tableActionsLeft={null}
      table={
        <DataTable
          data={data}
          onSortChange={onSortChange}
          handleAction={handleAction}
          tableProps={{
            size: 'sm',
          }}
        />
      }
      pagination={
        paginationData ? <Pagination pagination={paginationData} onPageChange={setPage} /> : null
      }
    />
  );
}

export function FilesListPage() {
  return (
    <FiltersProvider localStorageKey="files-list-filters">
      <FilesListContent />
    </FiltersProvider>
  );
}
